/* eslint-disable no-inner-declarations */
/* eslint-disable @typescript-eslint/no-namespace */
import { MapperHelpers } from '../mappers';
import _ from 'lodash';
import { DealVersion, Series, SecurityType } from '../../types/deal.types';

export const FROM_TO_DATE_REGEXP = /^(\d{1,2}\/\d{4})$/;

export namespace DealsSeries {
  export namespace Sector {
    export interface SubSector {
      label: string;
      sector: string;
      ignorable: boolean;
    }

    export interface SubSectorVM extends SubSector {
      isGreyedOut: boolean;
    }

    export interface SubSectorDictionary {
      [s: string]: SubSector;
    }
    export function getSubSectors(): SubSectorDictionary {
      return getSubSectorsDictionary();
    }

    function getSortedActiveWeekSubSectors(deals: DealVersion[]) {
      return MapperHelpers.getUniqSortedItemPropertiesFromArray(deals, {
        getProperty: (deal: DealVersion) => deal.series,
        getArrayProperty: (series: Series[]) =>
          series.reduce(
            (prev: string[], s: Series) => [...prev, ...s.subSectors],
            [] as string[]
          ),
        sortBy: (sector: string) => sector,
        uniqBy: (sector: string) => sector
      }).filter((code: string) => !getSubSectors()[code].ignorable);
    }

    export function getWeekSubSectors(deals: DealVersion[]): SubSectorVM[] {
      return getSortedActiveWeekSubSectors(deals).reduce(
        (prev: SubSectorVM[], code: string) =>
          getSubSectors()[code]
            ? [
                ...prev,
                {
                  ...getSubSectors()[code],
                  isGreyedOut: false
                }
              ]
            : prev,
        [] as SubSectorVM[]
      );
    }

    export function getAllSubSectors(deals: DealVersion[]): SubSectorVM[] {
      const weekSubSectors: {
        [key: string]: string;
      } = getSortedActiveWeekSubSectors(deals).reduce(
        (prev, subSector: string) => ({ ...prev, [subSector]: subSector }),
        {} as { [key: string]: string }
      );

      return _.sortBy<SubSectorVM>(
        Object.keys(getSubSectors())
          .filter((sectorKey: string) => !getSubSectors()[sectorKey].ignorable)
          .map((sectorKey: string) => ({
            ...getSubSectors()[sectorKey],
            isGreyedOut: !weekSubSectors[getSubSectors()[sectorKey].label]
          })),
        (sector: SubSectorVM) => sector.label
      );
    }

    export function getAllUniqSectorNames(): string[] {
      return Object.keys(getSubSectors())
        .reduce(
          (acc: string[], key: string) =>
            acc.indexOf(getSubSectors()[key].sector) === -1
              ? [...acc, getSubSectors()[key].sector]
              : acc,
          [] as string[]
        )
        .sort();
    }
    export function getAllUniqPurposes(): string[] {
      return Object.keys(getSubSectors())
        .reduce(
          (acc: string[], key: string) =>
            acc.indexOf(getSubSectors()[key].label) === -1 &&
            !getSubSectors()[key].ignorable
              ? [...acc, getSubSectors()[key].label]
              : acc,
          [] as string[]
        )
        .sort();
    }
  }
  export function getSecurityType(secType: SecurityType) {
    switch (secType) {
      case SecurityType.zero:
        return SecurityType.bond;
      case SecurityType.option:
        return SecurityType.put;
      default:
        return secType;
    }
  }
}

function getSubSectorsDictionary(): DealsSeries.Sector.SubSectorDictionary {
  return {
    501: {
      label: '501C (3)',
      sector: 'Education',
      ignorable: true
    },
    ASS: {
      label: 'Assessment District',
      sector: 'Economic Development',
      ignorable: false
    },
    ECO: {
      label: 'Economic Development',
      sector: 'Economic Development',
      ignorable: false
    },
    EIR: {
      label: 'Environmental Improvement',
      sector: 'Economic Development',
      ignorable: false
    },
    OFF: {
      label: 'Office Buildings',
      sector: 'Economic Development',
      ignorable: false
    },
    CHS: {
      label: 'Charter Schools',
      sector: 'Education',
      ignorable: false
    },
    DRM: {
      label: 'Dormitory',
      sector: 'Education',
      ignorable: false
    },
    EDU: {
      label: 'Education - Other',
      sector: 'Education',
      ignorable: false
    },
    LIB: {
      label: 'Libraries & Museums',
      sector: 'Education',
      ignorable: false
    },
    NPF: {
      label: 'Non-profit',
      sector: 'Education',
      ignorable: false
    },
    PRP: {
      label: 'Primary & Secondary Education',
      sector: 'Education',
      ignorable: false
    },
    PVC: {
      label: 'Private College/University',
      sector: 'Education',
      ignorable: false
    },
    PUC: {
      label: 'Public College/University',
      sector: 'Education',
      ignorable: false
    },
    STL: {
      label: 'Student Loans',
      sector: 'Education',
      ignorable: false
    },
    HIG: {
      label: 'Higher Education',
      sector: 'Education',
      ignorable: false
    },
    UNV: {
      label: 'Universities',
      sector: 'Education',
      ignorable: true
    },
    SCH: {
      label: 'Schools',
      sector: 'Education',
      ignorable: true
    },
    GGP: {
      label: 'Gen Purpose/Pub Impt',
      sector: 'General Purposes',
      ignorable: false
    },
    GEN: {
      label: 'General Obligation',
      sector: 'General Purposes',
      ignorable: true
    },
    LTG: {
      label: 'Limited Tax GO',
      sector: 'General Purposes',
      ignorable: true
    },
    PIT: {
      label: 'Personal Income Tax',
      sector: 'General Purposes',
      ignorable: true
    },
    REF: {
      label: 'Revolving Fund',
      sector: 'General Purposes',
      ignorable: true
    },
    SLT: {
      label: 'Sales Tax',
      sector: 'General Purposes',
      ignorable: true
    },
    UTG: {
      label: 'Unlimited Tax GO',
      sector: 'General Purposes',
      ignorable: true
    },
    CHI: {
      label: "Children's Hospital",
      sector: 'Health Care',
      ignorable: false
    },
    GAC: {
      label: 'General Acute Care Hospitals',
      sector: 'Health Care',
      ignorable: false
    },
    HLT: {
      label: 'Health Care - Other',
      sector: 'Health Care',
      ignorable: false
    },
    HOE: {
      label: 'Hospital Equipment',
      sector: 'Health Care',
      ignorable: false
    },
    CRC: {
      label: 'Lifecare/Retire Centers',
      sector: 'Health Care',
      ignorable: false
    },
    EHS: {
      label: 'Nursing Homes',
      sector: 'Health Care',
      ignorable: false
    },
    SSH: {
      label: 'Single Specialty Hospitals',
      sector: 'Health Care',
      ignorable: false
    },
    HOS: {
      label: 'Hospital',
      sector: 'Health Care',
      ignorable: true
    },
    HLO: {
      label: 'Health Care - Other',
      sector: 'Health Care',
      ignorable: false
    },
    LIH: {
      label: 'Low Income Housing',
      sector: 'Housing',
      ignorable: false
    },
    MFH: {
      label: 'Multi Family Housing',
      sector: 'Housing',
      ignorable: false
    },
    SFH: {
      label: 'Single Family Housing',
      sector: 'Housing',
      ignorable: false
    },
    HSG: {
      label: 'Housing - Other',
      sector: 'Housing',
      ignorable: false
    },
    IDR: {
      label: 'Industrial Development',
      sector: 'Industrial Development',
      ignorable: false
    },
    PCR: {
      label: 'Pollution Control',
      sector: 'Industrial Development',
      ignorable: false
    },
    RES: {
      label: 'Solid Waste/Resource Recovery',
      sector: 'Industrial Development',
      ignorable: false
    },
    COR: {
      label: 'Corporate',
      sector: 'Industrial Development',
      ignorable: true
    },
    EQU: {
      label: 'Equipment Leasing',
      sector: 'Miscellaneous',
      ignorable: false
    },
    HOM: {
      label: 'Hotel/Motel',
      sector: 'Miscellaneous',
      ignorable: false
    },
    MIS: {
      label: 'Miscellaneous',
      sector: 'Miscellaneous',
      ignorable: false
    },
    TOB: {
      label: 'Tobacco Settlement',
      sector: 'Miscellaneous',
      ignorable: false
    },
    TRI: {
      label: 'Tribal',
      sector: 'Miscellaneous',
      ignorable: false
    },
    APB: {
      label: 'Appropriation Bonds',
      sector: 'Miscellaneous',
      ignorable: true
    },
    ASB: {
      label: 'Asset Backed',
      sector: 'Miscellaneous',
      ignorable: true
    },
    BAN: {
      label: 'BANS',
      sector: 'Miscellaneous',
      ignorable: true
    },
    CAB: {
      label: 'CABS',
      sector: 'Miscellaneous',
      ignorable: true
    },
    CEF: {
      label: 'Closed End Fund',
      sector: 'Miscellaneous',
      ignorable: true
    },
    COO: {
      label: 'Certificate of Obligation',
      sector: 'Miscellaneous',
      ignorable: true
    },
    COP: {
      label: 'Certificates of Participation',
      sector: 'Miscellaneous',
      ignorable: true
    },
    COT: {
      label: 'Certificate of Obligation Limited Tax',
      sector: 'Miscellaneous',
      ignorable: true
    },
    DER: {
      label: 'Derivative',
      sector: 'Miscellaneous',
      ignorable: true
    },
    ESW: {
      label: 'Escrow',
      sector: 'Miscellaneous',
      ignorable: true
    },
    LQF: {
      label: 'Liquidity Facility',
      sector: 'Miscellaneous',
      ignorable: true
    },
    LSE: {
      label: 'Lease',
      sector: 'Miscellaneous',
      ignorable: true
    },
    LSR: {
      label: 'Lease Revenue',
      sector: 'Miscellaneous',
      ignorable: true
    },
    TAX: {
      label: 'Tax Increment',
      sector: 'Miscellaneous',
      ignorable: true
    },
    TRD: {
      label: 'Trust Receipts – Direct Purchase',
      sector: 'Miscellaneous',
      ignorable: true
    },
    TRP: {
      label: 'Trust Receipts – Portfolio',
      sector: 'Miscellaneous',
      ignorable: true
    },
    TRH: {
      label: 'Trust Receipts – Third Party',
      sector: 'Miscellaneous',
      ignorable: true
    },
    TRT: {
      label: 'Trust Receipts – Total Return Swaps',
      sector: 'Miscellaneous',
      ignorable: true
    },
    TRC: {
      label: 'Trust Receipts',
      sector: 'Miscellaneous',
      ignorable: true
    },
    IFD: {
      label: 'Infrastructure District',
      sector: 'Miscellaneous',
      ignorable: false
    },
    PEN: {
      label: 'Pension/Retirement/Disability',
      sector: 'Pension',
      ignorable: false
    },
    ELE: {
      label: 'Electric & Public Power',
      sector: 'Power',
      ignorable: false
    },
    GAS: {
      label: 'Gas',
      sector: 'Power',
      ignorable: false
    },
    MUD: {
      label: 'Municipal Utility District',
      sector: 'Power',
      ignorable: false
    },
    PWR: {
      label: 'Power - Other',
      sector: 'Power',
      ignorable: false
    },
    CEN: {
      label: 'Civic & Convention Centers',
      sector: 'Public Buildings',
      ignorable: false
    },
    FAC: {
      label: 'Correction Facs/Courts',
      sector: 'Public Buildings',
      ignorable: false
    },
    FIR: {
      label: 'Fire Station & Equipment',
      sector: 'Public Buildings',
      ignorable: false
    },
    GVB: {
      label: 'Govt/Pub Buildings',
      sector: 'Public Buildings',
      ignorable: false
    },
    PHS: {
      label: 'Public Health & Safety',
      sector: 'Public Buildings',
      ignorable: false
    },
    PKG: {
      label: 'Parking Facilities',
      sector: 'Public Buildings',
      ignorable: false
    },
    POL: {
      label: 'Police Station & Equipment',
      sector: 'Public Buildings',
      ignorable: false
    },
    LAE: {
      label: 'Public Safety',
      sector: 'Public Buildings',
      ignorable: false
    },
    VET: {
      label: 'Veterans (Excl. Housing)',
      sector: 'Public Buildings',
      ignorable: false
    },
    BLD: {
      label: 'Buildings',
      sector: 'Public Buildings',
      ignorable: true
    },
    AGR: {
      label: 'Agriculture',
      sector: 'Public Works',
      ignorable: false
    },
    IRR: {
      label: 'Irrigation',
      sector: 'Public Works',
      ignorable: false
    },
    LBF: {
      label: 'Land Based Financing',
      sector: 'Public Works',
      ignorable: false
    },
    RED: {
      label: 'Redevelopment Agency',
      sector: 'Public Works',
      ignorable: false
    },
    PAR: {
      label: 'Parks, Zoos & Beaches',
      sector: 'Recreation',
      ignorable: false
    },
    CAS: {
      label: 'Racetrack/Casino',
      sector: 'Recreation',
      ignorable: false
    },
    GOC: {
      label: 'Recreational Facilities',
      sector: 'Recreation',
      ignorable: false
    },
    STA: {
      label: 'Stadiums/Sports Complex',
      sector: 'Recreation',
      ignorable: false
    },
    THE: {
      label: 'Theatres',
      sector: 'Recreation',
      ignorable: false
    },
    AIR: {
      label: 'Airports',
      sector: 'Transportation',
      ignorable: false
    },
    HWY: {
      label: 'Highway',
      sector: 'Transportation',
      ignorable: false
    },
    MRT: {
      label: 'Mass/Rapid Transit',
      sector: 'Transportation',
      ignorable: false
    },
    BRI: {
      label: 'Roads/Tunnels/Bridges',
      sector: 'Transportation',
      ignorable: false
    },
    POR: {
      label: 'Seaports/Marine Terminals',
      sector: 'Transportation',
      ignorable: false
    },
    TRS: {
      label: 'Transportation - Other',
      sector: 'Transportation',
      ignorable: false
    },
    TRA: {
      label: 'Transportation',
      sector: 'Transportation',
      ignorable: true
    },
    UTL: {
      label: 'Combined Utilities',
      sector: 'Utilities - Other',
      ignorable: false
    },
    COM: {
      label: 'Communication',
      sector: 'Utilities - Other',
      ignorable: false
    },
    TEL: {
      label: 'Telephone',
      sector: 'Utilities - Other',
      ignorable: false
    },
    PWO: {
      label: 'Power - Other',
      sector: 'Utilities',
      ignorable: false
    },
    FLD: {
      label: 'Flood Control/Storm Drain',
      sector: 'Water & Sewer',
      ignorable: false
    },
    SAN: {
      label: 'Sanitation',
      sector: 'Water & Sewer',
      ignorable: false
    },
    SWR: {
      label: 'Sewer',
      sector: 'Water & Sewer',
      ignorable: false
    },
    WTR: {
      label: 'Water',
      sector: 'Water & Sewer',
      ignorable: false
    },
    WSG: {
      label: 'Water/Sewer/Gas',
      sector: 'Water & Sewer',
      ignorable: true
    },
    JDA: {
      sector: 'Economic Development',
      label: 'Economic Development',
      ignorable: false
    },
    FPD: {
      sector: 'Public Buildings',
      label: 'Fire Station & Equipment',
      ignorable: false
    },
    DRA: {
      sector: 'Water & Sewer',
      label: 'Flood Control/Storm Drain',
      ignorable: false
    },
    GIP: {
      sector: 'General Purposes',
      label: 'Gen Purpose/Pub Impt',
      ignorable: false
    },
    CIG: {
      sector: 'General Purposes',
      label: 'Gen Purpose/Pub Impt',
      ignorable: false
    },
    COG: {
      sector: 'General Purposes',
      label: 'Gen Purpose/Pub Impt',
      ignorable: false
    },
    LGA: {
      sector: 'General Purposes',
      label: 'Gen Purpose/Pub Impt',
      ignorable: false
    },
    MTD: {
      sector: 'General Purposes',
      label: 'Gen Purpose/Pub Impt',
      ignorable: false
    },
    STE: {
      sector: 'General Purposes',
      label: 'Gen Purpose/Pub Impt',
      ignorable: false
    },
    VAR: {
      sector: 'General Purposes',
      label: 'Gen Purpose/Pub Impt',
      ignorable: false
    },
    LIF: {
      sector: 'Health Care',
      label: 'Lifecare/Retire Centers',
      ignorable: false
    },
    NUR: {
      sector: 'Health Care',
      label: 'Nursing Homes',
      ignorable: false
    },
    AST: {
      sector: 'Health Care',
      label: 'Nursing Homes',
      ignorable: false
    },
    PRS: {
      sector: 'Education',
      label: 'Primary & Secondary Education',
      ignorable: false
    },
    POO: {
      sector: 'Recreation',
      label: 'Recreational Facilities',
      ignorable: false
    },
    REC: {
      sector: 'Recreation',
      label: 'Recreational Facilities',
      ignorable: false
    },
    RTB: {
      sector: 'Transportation',
      label: 'Roads/Tunnels/Bridges',
      ignorable: false
    },
    TUN: {
      sector: 'Transportation',
      label: 'Roads/Tunnels/Bridges',
      ignorable: false
    },
    SEA: {
      sector: 'Transportation',
      label: 'Seaports/Marine Terminals',
      ignorable: false
    },
    WTS: {
      sector: 'Transportation',
      label: 'Seaports/Marine Terminals',
      ignorable: false
    },
    SOL: {
      sector: 'Industrial Development',
      label: 'Solid Waste/Resource Recovery',
      ignorable: false
    },
    REV: {
      sector: 'General Purposes',
      label: 'Gen Purpose/Pub Impt',
      ignorable: true
    }
  };
}
